import { IOriginalSceneDocumentService } from "../../project/common/originalSceneDocumentService.js";
import type { IKeybindingService } from "../../../../platform/keybinding/common/keybindingService.js";
import { Disposable, IDisposable, toDisposable, DisposableStore } from "../../../../base/common/lifecycle";
import { IUndoRedoService } from "../../../../platform/undo/common/undo";
import { collectSubtree } from "../../operations/common/subtree";
import { IOperationProperty, operationValuesEqual } from "../../operations/common/operations";
import { safeStringify } from "../../../../base/common/objects";
import { toMaxFixed } from "../../../../base/common/humanize";
import { MenuId, MenuRegistry } from "../../../../platform/actions/common/actions";
import { IAnalyticsService } from "../../../../platform/analytics/common/analytics";
import { ContextKeyLoggedIn as IsLoggedInContextKey, IIdentitiyService } from "../../../../platform/auth/common/auth";
import { OpenUpsellDialogCommandId, type UpsellDialogOptions } from "../../../../platform/upsell/common/upsell";
import { ILicenseService } from "../../../../platform/auth/common/licenses.js";
import { hasInspectorAccess } from "../../../../platform/upsell/common/editEntitlement.js";
import { IProductService } from "../../../../platform/product/common/productService.js";
import { Command, CommandsRegistry, ICommandCanExecuteReturnValue, ICommandService } from "../../../../platform/commands/common/commands";
import { ContextKeyExpr, IContextKeyService } from "../../../../platform/contextkey/common/contextkey";
import { HierarchyNode } from "../../../../platform/hierarchy/common/hierarchyService";
import { ServicesAccessor } from "../../../../platform/instantiation/common/instantiation";
import { createDecorator } from "../../../../platform/instantiation/common/registry";
import { ILogService } from "../../../../platform/log/common/log";
import { INotificationService } from "../../../../platform/notifications/common/notifications";
import { IPropertyAccessorService } from "../../../../platform/properties/common/accessor";
import { IProperty, ValueMetadata } from "../../../../platform/properties/common/properties";
import { IProjectService } from "../../project/common/project";
import { resolveSourceAsset, SourceAssetRef } from "../../project/common/sourceAsset";
import { splitAddress } from "../../../../platform/properties/common/propertyPath";
// type-only: a runtime edge here closes the operations→properties→propertyCommands→
// changeset import cycle (see plans/operation-bridge.md 1f)
import type { OverTarget } from "../../project/common/documentTarget";

export const IChangesetService = createDecorator<IChangesetService>('changesetService');

export interface IChangesetService {
    readonly _serviceBrand: undefined;

    registerPropertyChange(property: IOperationProperty, oldValue?: any): void;
    registerChange(path: string, newValue: unknown, oldValue?: unknown, metadata?: ValueMetadata): void;

    exportChanges(format: ChangeFormat): Promise<CompiledChanges>;

    /**
     * The registered property changes (deduped per property). Readonly view for
     * consumers that translate edits into other representations — e.g. the project
     * document writer turning loaded-asset edits into overs (saveProjectCommand).
     */
    readonly propertyChanges: ReadonlyArray<IChangesetPropertyEntry>;
}

/** A registered property change, with its source-asset attribution. */
export interface IChangesetPropertyEntry {
    /** the LIVE property — `property.value`/`property.owner` are current state.
     *  Widened to the OP contract: not every edit has an inspector property row
     *  (gizmo drags describe themselves minimally) — see IOperationProperty. */
    readonly property: IOperationProperty;
    readonly key: string;
    readonly path: string;
    readonly oldValue: unknown;
    readonly newValue: unknown;
    /** which loaded asset (file + glTF element) the edit belongs to, if any */
    readonly source?: SourceAssetRef | null;
    /** the EXACT instance (provenance stamp of the owning node) — the same file
     * placed twice shares a `source.uri`, and containment cannot disambiguate a
     * node that was moved OUT of its root; the stamp can */
    readonly referenceId?: string;
    /**
     * DEEP-EDIT override: when `property.owner` is not attributable (texture-settings
     * rows use a synthetic delegate owner), the fold resolves the owning SCENE OBJECT +
     * the full deep path (e.g. "material/map/repeat") from `metadata.address` at op
     * time. Over-building targets THIS instead of the owner. Session ids never persist —
     * only the node address + path reach the document.
     */
    readonly deepTarget?: { readonly object: object; readonly path: string };
    /**
     * COMPOSITION-scene edit: the owner is a Scene no loaded file owns (the edit scene —
     * background color, fog, environment). Over-building targets the composition root
     * (`{ path }` with no ref).
     */
    readonly compositionTarget?: boolean;
    /** op order of the latest touch — the writer sorts all overs by this so "later
     * edit wins" holds across edit kinds (absent for non-fold producers → treated as 0) */
    readonly sequence?: number;
    /** the OP's target (address-only fold entries, 1g): when present the writer takes
     * the address rail — over target/path come from HERE and the live object is
     * re-resolved only for liveness; `property.owner`/`deepTarget` are never touched */
    readonly target?: OverTarget;
}

type ChangeFormat = "json" | "text";

/** True for a THREE.Color-like object (a bare number is ambiguous — only treated
 *  as a color when the property TYPE says so). */
function isColorLikeValue(v: unknown): boolean {
    return !!v && typeof v === "object"
        && ((v as { isColor?: boolean }).isColor === true || typeof (v as { getHexString?: unknown }).getHexString === "function");
}

/**
 * A color value (THREE.Color, `{r,g,b,a}`, or an sRGB hex integer) as an sRGB
 * `rgb()`/`rgba()` string — what the color picker shows, so old & new read
 * consistently. three stores color LINEARLY; `getHexString()`/`getHex()` are
 * sRGB, so we prefer those; a plain `{r,g,b}` is assumed already display-space.
 */
function formatColorValue(v: unknown): string | undefined {
    let r: number, g: number, b: number, a = 1;
    if (typeof v === "number") {
        r = (v >> 16) & 255; g = (v >> 8) & 255; b = v & 255; // sRGB hex integer (Color.getHex)
    }
    else if (v && typeof v === "object") {
        const c = v as { r?: number; g?: number; b?: number; a?: number; alpha?: number; getHexString?: () => string };
        if (typeof c.getHexString === "function") {
            const hex = c.getHexString(); // sRGB "rrggbb"
            r = parseInt(hex.slice(0, 2), 16); g = parseInt(hex.slice(2, 4), 16); b = parseInt(hex.slice(4, 6), 16);
        }
        else if (typeof c.r === "number") {
            r = to255(c.r); g = to255(c.g ?? 0); b = to255(c.b ?? 0);
        }
        else {
            return undefined;
        }
        a = c.a ?? c.alpha ?? 1;
    }
    else {
        return undefined;
    }
    a = Math.round((a ?? 1) * 1000) / 1000;
    return a < 1 ? `rgba(${r}, ${g}, ${b}, ${a})` : `rgb(${r}, ${g}, ${b})`;
}
function to255(x: number): number {
    return Math.max(0, Math.min(255, Math.round(x * 255)));
}

/**
 * A vector-like value (three's Vector2/3/4, Quaternion, Euler, or a plain number
 * array) as a plain number array — so both SIDES of a change read the same.
 * Producers legitimately hand in different shapes for the same property: the
 * transform command's "old" is a snapshot ARRAY while "new" is the live three
 * object, which rendered as `[0,0,0]` → `{"x":…,"y":…,"z":…}` and looked like two
 * unrelated things. Values are trimmed to 6 decimals — this is a human/LLM-facing
 * summary, and raw float noise (`-0.06026235700770966`) hurts more than it helps.
 */
function formatVectorValue(v: unknown): number[] | undefined {
    let values: number[] | undefined;
    if (Array.isArray(v) && v.every(n => typeof n === "number")) {
        values = v as number[];
    }
    else if (v && typeof v === "object" && typeof (v as { toArray?: unknown }).toArray === "function") {
        // exclude colors — those have their own sRGB rendering above
        if (isColorLikeValue(v)) return undefined;
        const asArray = (v as { toArray: () => unknown[] }).toArray();
        if (asArray.every(n => typeof n === "number")) values = asArray as number[];
    }
    if (!values?.length) return undefined;
    return values.map(n => parseFloat(toMaxFixed(n, 6)));
}
type CompiledChanges = { text: string, empty?: false } | { text: null, empty: true };

interface RegisteredPropertyData {
    property: IOperationProperty;
    path: string;
    key: string;
    oldValue: any;
    newValue: any;
    registeredAt?: number;
    lastModifiedAt?: number;
    /** Which loaded asset (file + glTF element) this edit belongs to, if any. */
    source?: SourceAssetRef | null;
}

export class ChangesetService extends Disposable implements IChangesetService {

    readonly _serviceBrand: undefined;

    constructor(
        @ILogService private readonly logger: ILogService,
        @IPropertyAccessorService private readonly propertyAccessorService: IPropertyAccessorService,
        @IContextKeyService contextKeyService: IContextKeyService,
        @IAnalyticsService private readonly analyticsService: IAnalyticsService,
        @IProjectService private readonly projectService: IProjectService,
        @IUndoRedoService undoRedo: IUndoRedoService,
    ) {
        super();

        // NOT an op-seam subscriber anymore (plans/operation-bridge.md 1f): ops are
        // ADDRESS-only and no longer carry the live property this summary reads —
        // and in the chrome product objects have no addresses at all until #38, so
        // there would be nothing to subscribe to. Every set-property-emitting
        // COMMAND (property.setValue, the transform gizmo, rename, tag edits, the
        // reparent TRS rewrite) calls registerPropertyChange at its one emission
        // point instead; a new edit command must do the same until #38 deletes
        // this service.
        this._register(toDisposable(() => {
            this.registeredProperties.clear();
        }));
        const changeserviceKey = contextKeyService.createKey('changesetServiceRegistered', true);
        this._register(toDisposable(() => {
            changeserviceKey.reset();
        }));
        // memory: entries hold LIVE property owners. When a delete permanently leaves
        // the undo history, edits on the dead subtree are meaningless — prune them so
        // this registry never retains a deleted model (it was one of the leak paths).
        this._register(undoRedo.onDidDisposeElement(({ element }) => {
            const data = element.data as { kind?: string; owner?: object } | undefined;
            if (data?.kind !== "delete-node" || !data.owner) return;
            const dead = collectSubtree(data.owner);
            for (const [key, entry] of this.registeredProperties) {
                const owner = entry.property?.owner as object | undefined;
                if (owner && dead.has(owner)) this.registeredProperties.delete(key);
            }
        }));
    }

    private registeredProperties: Map<string, RegisteredPropertyData> = new Map();

    get propertyChanges(): ReadonlyArray<IChangesetPropertyEntry> {
        return [...this.registeredProperties.values()];
    }

    registerPropertyChange(property: IOperationProperty, previousValue?: any): void {

        // Check if already registered and update
        const path = this.tryGetPath(property, property.owner) || (property.owner as any).name || '<unknown>';
        const key = path + '.' + property.key;

        const prevRegistered = this.registeredProperties.get(key);
        // We're only interested in the first old value so we can show the full change at the end.
        // At export time we can also filter out no-op changes.
        const newValue = property.value;
        let oldValue = prevRegistered?.oldValue !== undefined ? prevRegistered.oldValue : previousValue;
        const registeredAt = prevRegistered?.registeredAt || Date.now();
        const lastModifiedAt = Date.now();
        if (!prevRegistered) {
            // `cloneValue` is optional here on purpose: ops can carry a lightweight
            // property descriptor (the transform command builds one per changed
            // key), and those always supply `previousValue` themselves.
            oldValue = previousValue !== undefined ? previousValue : property.cloneValue?.();
            this.logger.debug(`ChangesetService: Registering new property change as ${key} from ${oldValue} to ${newValue}`);
        }
        else this.logger.debug(`ChangesetService: Property change registered as: ${key} from ${oldValue} to ${newValue}`);
        // resolve the source asset once, at registration — the owner is
        // guaranteed live here (it may be collected by export time). Keep the
        // first resolution; re-edits of the same property don't change it.
        const source = prevRegistered?.source !== undefined
            ? prevRegistered.source
            : this.tryResolveSource(property.owner);
        this.registeredProperties.set(key, {
            property: property,
            path: path!,
            key: property.key,
            oldValue: oldValue,
            newValue: newValue,
            registeredAt,
            lastModifiedAt,
            source,
        });
    }

    private registeredChanges: Map<string, { path: string, oldValue: any, newValue: any, metadata?: unknown }> = new Map();

    registerChange(path: string, newValue: unknown, oldValue?: unknown, metadata?: unknown): void {
        const key = path;

        const prevRegistered = this.registeredChanges.get(key);
        // We're only interested in the first old value so we can show the full change at the end.
        // At export time we can also filter out no-op changes.
        let initialValue = prevRegistered?.oldValue !== undefined ? prevRegistered.oldValue : oldValue;
        if (!prevRegistered) {
            initialValue = oldValue;
            this.logger.debug(`ChangesetService: Registering new change as ${key} from ${initialValue} to ${newValue}`);
        }
        else this.logger.debug(`ChangesetService: Change registered as: ${key} from ${initialValue} to ${newValue}`);
        this.registeredChanges.set(key, {
            path,
            oldValue: initialValue,
            newValue,
            metadata
        });
    }

    async exportChanges(format: ChangeFormat): Promise<CompiledChanges> {

        // Gather only changed properties
        const changedProperties = new Map<string, RegisteredPropertyData>();
        const otherChanges = new Map<string, { path: string, oldValue: any, newValue: any, metadata?: unknown }>();

        // Filter out no-op changes
        this.registeredProperties.forEach((change, key) => {
            // not `property.equalsValue(...)` directly: an op property need not
            // implement it (only inspector properties do) — see IOperationProperty
            if (!operationValuesEqual(change.property, change.oldValue, change.newValue)) {
                changedProperties.set(key, change);
            }
        });

        this.registeredChanges.forEach((change, key) => {
            if (change.oldValue !== change.newValue) {
                otherChanges.set(key, change);
            }
        });

        if (changedProperties?.size === 0 && otherChanges.size === 0) {
            this.logger.info("ChangesetService: No changes to export.");
            this.analyticsService?.logEvent('changeset.exported', { format, changeCount: 0 });
            return { empty: true, text: null }
        }

        this.analyticsService?.logEvent('changeset.exported', { format, changeCount: changedProperties.size });

        const url = typeof window !== undefined ? window.location.href : undefined;

        // TODO: need a better way to ensure serialized values are not too large (e.g. textures). Maybe we only need the metadata if we have it?

        switch (format) {
            case "text": {
                // group edits by the loaded asset they belong to; edits with no
                // known source (scene content / non-glTF) fall in a final bucket
                const groups = new Map<string, { title: string; changes: RegisteredPropertyData[] }>();
                for (const [, change] of changedProperties) {
                    const bucket = change.source?.uri.toString() ?? "";
                    const title = change.source?.fileName ?? "Unattributed (scene content / not from a loaded file)";
                    if (!groups.has(bucket)) groups.set(bucket, { title, changes: [] });
                    groups.get(bucket)!.changes.push(change);
                }

                let result = `${changedProperties.size} changed properties in ${url}\n\n`;
                for (const group of groups.values()) {
                    result += `## ${group.title}\n\n`;
                    for (const change of group.changes) {
                        const info = await this.getChangeInfo(change);
                        result += `Path: ${change.path}`;
                        if (info.id) {
                            result += `, ID: ${info.id}`;
                        }
                        if (change.source?.gltf) {
                            result += `, glTF: ${change.source.gltf.kind}[${change.source.gltf.index}]`;
                        }
                        result += `\nProperty: ${change.key}`;
                        if (info.oldValueMetadata) {
                            result += `\n- Old: ${safeStringify(info.oldValueMetadata)}`;
                        }
                        else {
                            result += `\n- Old: ${safeStringify(info.oldValue)}`;
                        }
                        if (info.newValueMetadata) {
                            result += `\n- New: ${safeStringify(info.newValueMetadata)}`;
                        }
                        else {
                            result += `\n- New: ${safeStringify(info.newValue)}`;
                        }
                        result += `\n\n`;
                    }
                }
                for (const [, change] of otherChanges) {
                    result += `Path: ${change.path}\n`;
                    if (change.metadata) {
                        result += `- New: ${safeStringify(change.metadata)}\n`;
                    }
                    else {
                        result += `- Old Value: ${safeStringify(change.oldValue)}\n`;
                        result += `- New Value: ${safeStringify(change.newValue)}\n`;
                    }
                    result += `\n`;
                }
                return { text: result };
            }

            case "json": {
                const json = {
                    url: url,
                    exportedAt: Date.now(),
                    // change keys grouped by the source asset they belong to
                    // (fileName → [change key]); "" = unattributed
                    byFile: {} as Record<string, string[]>,
                    changes: {} as Record<string, unknown>
                }
                for (const [, change] of changedProperties) {
                    const info = await this.getChangeInfo(change);
                    const changeKey = `${change.path}.${change.key}`;
                    json.changes[changeKey] = info;
                    const fileKey = change.source?.fileName ?? "";
                    (json.byFile[fileKey] ??= []).push(changeKey);
                };
                for (const [, change] of otherChanges) {
                    const newValueMetadata = change.metadata;
                    json.changes[change.path] = {
                        metadata: newValueMetadata ? { ...newValueMetadata } : undefined,
                        oldValue: newValueMetadata ? undefined : change.oldValue,
                        newValue: newValueMetadata ? undefined : change.newValue,
                    }
                };
                return { text: safeStringify(json) };
            }
        }

        throw new Error(`Unsupported change format: ${format}`);
    }

    private async getChangeInfo(change: RegisteredPropertyData) {
        const node = HierarchyNode.get(change.property.owner);
        let id: string | undefined = undefined;
        let name: string | undefined = undefined;
        if (node) {
            id = node.id;
            name = node.name;
        }

        // Color edits: render BOTH sides as sRGB rgb()/rgba() (what the picker
        // shows) instead of three's Color.toJSON() hex integer — consistent and
        // readable for humans + LLMs consuming the changeset.
        if (change.property.type === "color" || isColorLikeValue(change.oldValue) || isColorLikeValue(change.newValue)) {
            const oldColor = formatColorValue(change.oldValue);
            const newColor = formatColorValue(change.newValue);
            if (oldColor !== undefined || newColor !== undefined) {
                return {
                    id,
                    source: this.sourceInfo(change),
                    registeredAt: change.registeredAt,
                    lastModifiedAt: change.lastModifiedAt !== change.registeredAt ? change.lastModifiedAt : undefined,
                    oldValue: oldColor ?? change.oldValue,
                    newValue: newColor ?? change.newValue,
                };
            }
        }

        // Enum edits (op/method/toneMapping/colorSpace/…): show the human LABEL
        // from the property's enumOptions, keeping the raw value for precision —
        // "+" → `Add (+)`, 2 → `Reinhard (2)` — instead of a cryptic symbol/number.
        const enumOptions = change.property.metadata?.enumOptions;
        if (enumOptions?.length) {
            const withLabel = (v: unknown): string => {
                const opt = enumOptions.find(o => String(o.value) === String(v));
                const label = opt ? (typeof opt.label === "string" ? opt.label : String(opt.label.value)) : undefined;
                return label !== undefined ? `${label} (${safeStringify(v)})` : safeStringify(v);
            };
            return {
                id,
                source: this.sourceInfo(change),
                registeredAt: change.registeredAt,
                lastModifiedAt: change.lastModifiedAt !== change.registeredAt ? change.lastModifiedAt : undefined,
                oldValue: withLabel(change.oldValue),
                newValue: withLabel(change.newValue),
            };
        }

        const metadatas = await Promise.allSettled([
            this.propertyAccessorService.readMetadata?.(change.oldValue),
            this.propertyAccessorService.readMetadata?.(change.newValue)
        ]);
        const oldMetadataStr = metadatas[0].status === "fulfilled" ? metadatas[0].value : undefined;
        const newMetadataStr = metadatas[1].status === "fulfilled" ? metadatas[1].value : undefined;
        return {
            id,
            source: this.sourceInfo(change),
            registeredAt: change.registeredAt,
            lastModifiedAt: change.lastModifiedAt !== change.registeredAt ? change.lastModifiedAt : undefined,
            // putting metadata first so it gets serialized first and we clone it so if it exists multiple times we don't serialize with [Circular]
            oldValueMetadata: oldMetadataStr ? {...oldMetadataStr } : undefined,
            newValueMetadata: newMetadataStr ? {...newMetadataStr } : undefined,
            oldValue: oldMetadataStr ? undefined : formatVectorValue(change.oldValue) ?? change.oldValue,
            newValue: newMetadataStr ? undefined : formatVectorValue(change.newValue) ?? change.newValue,
        };
    }

    /** which loaded asset (file + glTF element) an edit maps to, if any */
    private sourceInfo(change: RegisteredPropertyData) {
        return change.source ? {
            file: change.source.uri.toString(),
            fileName: change.source.fileName,
            gltf: change.source.gltf,
            via: change.source.via,
        } : undefined;
    }

    private tryResolveSource(owner: object): SourceAssetRef | null {
        try {
            return resolveSourceAsset(this.projectService.openFiles, owner);
        }
        catch (e) {
            this.logger.debug(`ChangesetService: source resolution failed: ${e}`);
            return null;
        }
    }

    /** Short human label for a change's source, e.g. "helmet.glb › materials[2]". */
    private static sourceLabel(source: SourceAssetRef | null | undefined): string | undefined {
        if (!source) return undefined;
        return source.gltf ? `${source.fileName} › ${source.gltf.kind}[${source.gltf.index}]` : source.fileName;
    }

    private tryGetPath(property: IOperationProperty, object: object): string {
        let path = "";

        // optional: an op property need not carry metadata at all (gizmo drags)
        if (property.metadata?.address) {
            const address = property.metadata.address;
            // A deep address is rooted at a scene object but only carries its
            // session ID ("218/material/color"), which reads as nothing next to a
            // transform row on the same mesh ("/Scene/Group/Group/Mesh"). When the
            // provider recorded the object, name it and keep the sub-path.
            const addressOwner = property.metadata.addressOwner;
            if (addressOwner) {
                const sub = splitAddress(address)?.path ?? address;
                const ownerPath = this.tryGetPath({ key: "", owner: addressOwner, value: undefined }, addressOwner);
                if (ownerPath) return `${ownerPath} › ${sub}`;
            }
            return address;
        }

        // TODO: we need a better way to address properties
        // since these could be props in materials or components...
        // so properties should ideally provide their own addressing scheme

        // first try traversing parents if available — names, so the path reads the
        // way the hierarchy does ("/Editmode Scene/Content/Sphere")
        const edited = object;
        while ("parent" in object) {
            const obj = object as any;
            path = `/${obj.name || obj.type}${path}`;
            if (!obj.parent) break;
            object = obj.parent;
        }

        // fallback is traversing the node hierarchy
        if (!path?.length) {
            let node = HierarchyNode.get(edited);
            if (node) {
                while (node) {
                    path = `/${node.name || (`${node.type}:${node.id}`)}${path}`;
                    node = node.parent;
                }
                return path;
            }
        }

        // LAST resort only. This used to run unconditionally, throwing away the
        // name path built above — and since the walk reassigns `object`, it
        // described the walk's ROOT rather than the edited object, so a moved
        // "Sphere" was reported as "/19 (Editmode Scene)".
        if (!path?.length && "id" in edited) {
            const obj = edited as any;
            path = `/${obj.id}`;
            if ("type" in edited) {
                path += ` (${obj.name || obj.type})`;
            }
        }

        return path;
    }
}


export class GetChangesCommand extends Command {
    static readonly ID = 'changeset.download';

    constructor() {
        super(GetChangesCommand.ID, 'Get Changes', {
            description: 'Export current edits as a document or legacy changeset',
        });
    }

    canExecute(args: unknown): ICommandCanExecuteReturnValue {
        return { canExecute: true };
    }

    async execute(accessor: ServicesAccessor, args: { format?: ChangeFormat | "document"; sceneRef?: string; download?: boolean } = {}) {
        const changesetService = accessor.get(IChangesetService);
        const notificationService = accessor.getIfExists(INotificationService);
        const format = args.format ?? (accessor.getIfExists(IOriginalSceneDocumentService) ? "document" : "text");

        const auth = accessor.getIfExists(IIdentitiyService);
        const license = accessor.getIfExists(ILicenseService);
        // Changeset download is the take-home moment of an edit session —
        // license-gated everywhere (public sites included).
        if (!hasInspectorAccess(license)) {
            accessor.getIfExists(IAnalyticsService)?.logEvent("changeset.download.license_required", { format, loggedIn: auth?.isLoggedIn === true });
            const commandService = accessor.getIfExists(ICommandService);
            if (commandService && CommandsRegistry.getCommand(OpenUpsellDialogCommandId)) {
                const options: UpsellDialogOptions = {
                    source: "changeset_download",
                    requiresLicense: true,
                    title: "Save your edits with Pro",
                    subtitle: "Download a reusable edits file for this scene.",
                    message: "Saving your edits is part of Needle Inspector Pro. Sign in with a free account to continue.",
                    purchaseLabel: "Get Needle Inspector Pro",
                    // re-run this download once unlocked
                    continueCommand: { id: GetChangesCommand.ID, args },
                };
                commandService.executeCommand(OpenUpsellDialogCommandId, options);
            }
            else {
                // headless/agent builds run without the editor UI that renders the upsell dialog
                notificationService?.showNotification({
                    message: "Downloading your edits requires Needle Inspector Pro.",
                    type: "warning",
                    position: "cursor",
                });
            }
            return;
        }

        if (format === "document") return accessor.get(IOriginalSceneDocumentService).export({ ...args, allowPartial: true });

        const changes = await changesetService.exportChanges(format);
        if (changes.empty === true) {
            notificationService?.showNotification({
                message: 'No changes recorded. Property modifications are tracked. Only actual changes are included in the changeset.',
                type: 'warning',
                position: "cursor"
            });
            return;
        }
        // the "text" format is markdown (## file headers, etc.) → .md
        const extension = format === "json" ? "json" : "md";
        const mimeType = format === "json" ? "application/json" : "text/markdown";

        const text = changes.text as string;
        const blob = new Blob([text], { type: mimeType });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `changeset.${extension}`;
        a.click();
        URL.revokeObjectURL(url);
        notificationService?.showNotification({
            message: "Legacy changeset exports will be removed. Use File > Save Edits and File > Load Edits instead.",
            type: "info",
            duration: 60_000,
        });
    }
}
CommandsRegistry.registerCommand(new GetChangesCommand());

const downloadChangesetTxtId = "changeset.download.txt";
CommandsRegistry.registerCommand(downloadChangesetTxtId, (accessor: ServicesAccessor) => {
    const commandService = accessor.getIfExists(ICommandService);
    return commandService?.executeCommand(GetChangesCommand.ID, { format: "text" });
});
const downloadChangesetJsonId = "changeset.download.json";
CommandsRegistry.registerCommand(downloadChangesetJsonId, (accessor: ServicesAccessor) => {
    const commandService = accessor.getIfExists(ICommandService);
    return commandService?.executeCommand(GetChangesCommand.ID, { format: "json" });
});

MenuRegistry.appendMenuItem(MenuId.MenubarFileExportLegacyMenu, {
    command: {
        id: downloadChangesetTxtId,
        title: 'Edits (Markdown)',
        icon: { id: "export" },
        tooltip: 'Download your changes in the editor as a Markdown (.md) file — grouped by source asset. Can be used to review or share your edits or to pass them into an LLM.',
        // precondition: ContextKeyExpr.equals(IsLoggedInContextKey, true),
        // toggled: {
        //     condition: ContextKeyExpr.equals(IsLoggedInContextKey, false),
        //     title: 'Download Edits as Text (Requires Login)',
        // }
    },
    order: 1,
    when: ContextKeyExpr.equals('changesetServiceRegistered', true),
});

MenuRegistry.appendMenuItem(MenuId.MenubarFileExportLegacyMenu, {
    command: {
        id: downloadChangesetJsonId,
        title: 'Edits (JSON)',
        icon: { id: "export" },
        tooltip: 'Download your changes in the editor as a JSON file. Can be used to review or share your edits or to pass them into an LLM.',
        // precondition: ContextKeyExpr.equals(IsLoggedInContextKey, true),
        // toggled: {
        //     condition: ContextKeyExpr.equals(IsLoggedInContextKey, false),
        //     title: 'Download Edits as JSON (Requires Login)',
        // }
    },
    order: 1,
    when: ContextKeyExpr.equals('changesetServiceRegistered', true),
});
const downloadDocumentId = "changeset.download.document";
export class SaveEditsCommand extends Command {
    static readonly ID = downloadDocumentId;

    constructor() {
        super(SaveEditsCommand.ID, "Save Edits", {
            description: "Save the current edits as a reusable document, or return its JSON for inspection.",
            args: { name: "args", schema: { type: "object", properties: {
                returnJson: {
                    type: "boolean",
                    description: "Return the document JSON instead of downloading it.",
                },
            } } },
            aiName: "changeset_save_edits",
        });
    }

    canExecute(): ICommandCanExecuteReturnValue {
        return { canExecute: true };
    }

    execute(accessor: ServicesAccessor) {
        return accessor.get(ICommandService).executeCommand(GetChangesCommand.ID, { format: "document" });
    }

    async executeForAI(accessor: ServicesAccessor, args: { returnJson?: boolean } = {}): Promise<string> {
        if (args.returnJson && accessor.getIfExists(IProductService)?.unlicensedPermissions?.["edits.readDocument"] === true) {
            const result = await accessor.get(IOriginalSceneDocumentService).export({ download: false, allowPartial: true });
            if (!result?.document) throw new Error("No edits document was saved.");
            return JSON.stringify(result.document, null, 2);
        }
        const commandArgs = args.returnJson
            ? { format: "document" as const, download: false }
            : { format: "document" as const };
        const outcome = await accessor.get(ICommandService)
            .executeCommand<GetChangesCommand>(GetChangesCommand.ID, commandArgs, { isAi: false });
        if (!outcome.success) throw new Error(outcome.message || "Saving edits failed.");
        const result = await outcome.result;
        if (!result?.document) throw new Error("No edits document was saved.");
        if (args.returnJson) return JSON.stringify(result.document, null, 2);
        return "Saved the current edits as a reusable document.";
    }
}
CommandsRegistry.registerCommand(new SaveEditsCommand());
MenuRegistry.appendMenuItem(MenuId.MenubarFileMenu, {
    command: { id: downloadDocumentId, title: "Save Edits", icon: { id: "save" },
        tooltip: "Download your edits so they can be applied to the original scene." },
    group: "3_save",
    order: 0,
    when: ContextKeyExpr.equals("originalSceneDocumentExport", true),
});
const loadDocumentId = "document.load";
CommandsRegistry.registerCommand(loadDocumentId, (accessor: ServicesAccessor) => accessor.get(IOriginalSceneDocumentService).load());
MenuRegistry.appendMenuItem(MenuId.MenubarFileMenu, {
    command: { id: loadDocumentId, title: "Load Edits", icon: { id: "folder_open" }, tooltip: "Apply saved edits to the inspected scene." },
    group: "3_save",
    order: 1,
    when: ContextKeyExpr.equals("originalSceneDocumentExport", true),
});
MenuRegistry.appendMenuItem(MenuId.MenubarFileExportMenu, {
    title: "Legacy", submenu: MenuId.MenubarFileExportLegacyMenu,
    when: ContextKeyExpr.equals("changesetServiceRegistered", true),
});

export function registerOriginalSceneDocumentKeybinding(keybindings: IKeybindingService) {
    // Registered only by the Chrome product; menu context keys belong to a different service.
    const registrations = new DisposableStore();
    registrations.add(keybindings.registerKeybinding({ id: "keybinding.saveDocument", key: "Ctrl+S", commandId: downloadDocumentId,
        preventDefault: true, allowOnCanvas: true }));
    registrations.add(keybindings.registerKeybinding({ id: "keybinding.loadDocument", key: "Ctrl+O", commandId: loadDocumentId,
        preventDefault: true, allowOnCanvas: true }));
    return registrations;
}
